import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import { cn } from 'src/domains/common/components/utils';

class IndependentTooltipState {
    public triggerEl: HTMLElement | null = null;
    @observable public isVisible: boolean = false;

    public constructor() {
        makeObservable(this);
    }

    public setTriggerEl = (el: HTMLElement | null): void => {
        this.triggerEl = el;
    };

    public positionTooltip = (el: HTMLElement | null, position: 'top' | 'bottom'): void => {
        if (el === null || this.triggerEl === null) return;
        const rect = this.triggerEl.getBoundingClientRect();
        el.style.top = `${position === 'top' ? rect.top : rect.bottom}px`;
        el.style.left = `${rect.left + rect.width / 2}px`;
    };

    @action public show = (): void => {
        this.isVisible = true;
    };

    @action public hide = (): void => {
        this.isVisible = false;
    };
}

type IndependentTooltipPropsType = {
    children: React.ReactNode;
    content: React.ReactNode;
    position?: 'top' | 'bottom';
    underline?: boolean;
};

export const IndependentTooltip = observer(
    'IndependentTooltip',
    ({ children, content, position = 'top', underline = true }: IndependentTooltipPropsType) => {
        const [state] = useState(() => new IndependentTooltipState());

        return (
            <span
                ref={state.setTriggerEl}
                className={cn('relative inline-block w-fit cursor-pointer', underline && 'underline decoration-dotted')}
                onMouseEnter={state.show}
                onMouseLeave={state.hide}
            >
                {children}
                {state.isVisible &&
                    ReactDOM.createPortal(
                        <div
                            ref={(el) => state.positionTooltip(el, position)}
                            className={cn(
                                'fixed w-max max-w-xs bg-gray-100 text-black text-xs font-medium px-3 py-2 rounded shadow-md z-50 pointer-events-none text-center',
                                position === 'top'
                                    ? '-translate-x-1/2 -translate-y-full -mt-2'
                                    : '-translate-x-1/2 mt-2'
                            )}
                        >
                            {content}
                            <span
                                className={cn(
                                    'absolute w-2 h-2 bg-gray-100 rotate-45',
                                    position === 'top'
                                        ? '-bottom-1 left-1/2 -translate-x-1/2 shadow-md'
                                        : '-top-1 left-1/2 -translate-x-1/2 shadow-md'
                                )}
                            />
                        </div>,
                        document.body
                    )}
            </span>
        );
    }
);
